import { Copy } from "lucide-react";
import { useState } from "react";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../components/buttons/SecondaryButton";
import { CalendlyExpert } from "../../../utils/api-objects/CalendlyExpert";
import { Patient } from "../../../utils/api-objects/Patient";
import { assignMedicalUserToPatient } from "../../../utils/apis/medical-user-service";
import { useAuth } from "../../../utils/context/AuthContext";
import { ConsultType, TrackingStatus } from "../../../utils/Enums";
import { useGetNutritionists } from "../../experts/hooks/useGetNutritionists";
import { useGetPhysicians } from "../../experts/hooks/useGetPhysicians";
import { useRemovePatientFromMedicalUser } from "../../experts/hooks/useRemovePatientFromMedicalUser";
import { getExpertSchedulingUrl } from "../../experts/utils/getExpertSchedulingUrl";
import { getMedicalUserIdByEmail } from "../../experts/utils/getMedicalUserIdByEmail";
import { copyToClipboard } from "../../shared/utils/copyToClipboard";
import { useGetConsultsLeft } from "../hooks/useGetConsultsLeft";
import { useGetDnaTracker } from "../hooks/useGetDnaTracker";
import { useIncreaseConsultsLeft } from "../hooks/useIncreaseConsultsLeft";
import { useReduceConsultsLeft } from "../hooks/useReduceConsultsLeft";
import { useUpdateDnaTrackerByOrderId } from "../hooks/useUpdateDnaTrackerByOrderId";
import { useUpdateSwabcode } from "../hooks/useUpdateSwabcode";

const AdminBox: React.FC<{ patient: Patient }> = ({ patient }) => {
    const {
        nutritionistConsultsLeft,
        specialistConsultsLeft,
        refetch: refetchGetConsults
    } = useGetConsultsLeft(patient.user_id)

    const { dnaOrderId, refetch: refetchDnaTracker } = useGetDnaTracker(patient.user_id)
    const increase = useIncreaseConsultsLeft()
    const reduce = useReduceConsultsLeft()
    const updateDnaTracker = useUpdateDnaTrackerByOrderId()
    const updateSwabcode = useUpdateSwabcode();

    const nutritionistUrl = getExpertSchedulingUrl(patient.nutritionist.email || "") || ""
    const physicianUrl = getExpertSchedulingUrl(patient.physician.email || "") || ""
    const elvisUrl = getExpertSchedulingUrl("elvira.pistolesi@holifya.com") || ""

    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = () => {
        setIsExpanded(prev => !prev);
    };

    return (
        <div className="space-y-[25px] border rounded-xl p-2 border border-solid border-holifya-grey-shadow shadow-platform-new">
            {/* Header */}
            <div className="font-bold text-[16px] leading-[33px] flex justify-between items-center">
                <span>Area Admin</span>
                <button
                    onClick={handleToggle}
                    className="text-blue-500 text-sm">
                    {isExpanded ? "Mostra meno" : "Mostra di più"}
                </button>
            </div>

            {isExpanded &&
                <div className="space-y-[20px]">

                    {/* Section 1: Residual Consults */}
                    <div className="text-xs">
                        <div>Consulti residui con nutrizionista: {nutritionistConsultsLeft}</div>
                        <div>Consulti residui con specialista (validi sia per Elvis che per Endocrinologo): {specialistConsultsLeft}</div>
                    </div>

                    {/* Section 2: Modify Consults */}
                    <EditConsultsLeftSection
                        patientId={patient.user_id}
                        onUpdate={refetchGetConsults}
                        increase={increase}
                        reduce={reduce}
                    />

                    {/* Section 3: Booking Links */}
                    <ConsultBookingSection
                        patient={patient}
                        nutritionistUrl={nutritionistUrl}
                        physicianUrl={physicianUrl}
                        elvisUrl={elvisUrl}
                        nutritionistConsultsLeft={nutritionistConsultsLeft}
                        specialistConsultsLeft={specialistConsultsLeft}
                    />

                    {/* Section 4: Reset Swabcode */}
                    <SwabResetSection
                        patient={patient}
                        dnaOrderId={dnaOrderId}
                        updateDnaTracker={updateDnaTracker}
                        updateSwabcode={updateSwabcode}
                        refetchDnaTracker={refetchDnaTracker}
                    />

                    <ChangeExpertSection patient={patient} />
                </div>
            }
        </div>
    )
}

export default AdminBox

const EditConsultsLeftSection = ({
    patientId,
    increase,
    reduce,
    onUpdate,
}: {
    patientId: string;
    increase: any;
    reduce: any;
    onUpdate: () => void;
}) => {
    const confirmAndRun = (message: string, action: () => void) => {
        const confirmed = window.confirm(message);
        if (confirmed) action();
    };

    return (
        <div className="space-y-[10px]">
            <div className="font-bold text-lg">Modifica consulti</div>
            {["Nutritionist", "Specialist"].map((type) => {
                const label = type === "Nutritionist" ? "nutrizionista" : "specialista";
                const consultType = type === "Nutritionist" ? ConsultType.Nutritionist : ConsultType.Specialist;

                return (
                    <div key={type} className="flex gap-4">
                        <div className="w-[200px] h-[40px]">
                            <PrimaryButton
                                text={`+1 consulto con ${label}`}
                                pointer
                                disabled={false}
                                onClick={() =>
                                    confirmAndRun(`Aggiungere un consulto con ${label}?`, () =>
                                        increase(patientId, consultType, onUpdate, console.error)
                                    )
                                }
                                fontSize={10}
                            />
                        </div>
                        <div className="w-[200px] h-[40px]">
                            <SecondaryButton
                                text={`-1 consulto con ${label}`}
                                pointer
                                disabled={false}
                                onClick={() =>
                                    confirmAndRun(`Rimuovere un consulto con ${label}?`, () =>
                                        reduce(patientId, consultType, onUpdate, console.error)
                                    )
                                }
                                fontSize={10}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    )
}

interface ConsultBookingSectionProps {
    patient: Patient;
    nutritionistUrl: string;
    physicianUrl: string;
    elvisUrl: string;
    nutritionistConsultsLeft: number | null;
    specialistConsultsLeft: number | null;
}

const ConsultBookingSection: React.FC<ConsultBookingSectionProps> = ({
    patient,
    nutritionistUrl,
    physicianUrl,
    elvisUrl,
    nutritionistConsultsLeft,
    specialistConsultsLeft,
}) => {
    return (
        <div className="space-y-[10px]">
            <div className="font-bold text-lg">Prenota consulti</div>
            <div className="text-xs">
                Per prenotare ricorda che devi copiare la mail:&nbsp;
                <span className="inline-flex items-center text-holifya-blue relative group">
                    {patient.email}
                    <div className="relative ml-2 cursor-pointer">
                        <Copy
                            onClick={() => copyToClipboard(patient.email)}
                            className="text-blue-500 hover:text-blue-700 w-4 h-4"
                        />
                        <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-[10px] rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                            Copia email
                        </div>
                    </div>
                </span>
            </div>

            {!!nutritionistConsultsLeft && nutritionistConsultsLeft > 0 && patient.nutritionist.name && (
                <div className="w-[200px] h-[40px]">
                    <PrimaryButton
                        text={`Prenota consulto con ${patient.nutritionist.name}`}
                        pointer
                        disabled={false}
                        onClick={() => window.open(nutritionistUrl, "_blank")}
                        fontSize={10}
                    />
                </div>
            )}

            {!!specialistConsultsLeft && specialistConsultsLeft > 0 &&
                (patient.physician.name ? (
                    <div className="w-[200px] h-[40px]">
                        <PrimaryButton
                            text={`Prenota consulto con ${patient.physician.name}`}
                            pointer
                            disabled={false}
                            onClick={() => window.open(physicianUrl, "_blank")}
                            fontSize={10}
                        />
                    </div>
                ) : (
                    <div className="w-[200px] h-[40px]">
                        <PrimaryButton
                            text={`Prenota consulto con Elvira`}
                            pointer
                            disabled={false}
                            onClick={() => window.open(elvisUrl, "_blank")}
                            fontSize={10}
                        />
                    </div>
                ))}
        </div>
    );
};

const SwabResetSection = ({
    patient,
    dnaOrderId,
    updateDnaTracker,
    updateSwabcode,
    refetchDnaTracker,
}: any) => {
    const shouldShowReset = patient.swabcode && !patient.swabcode.startsWith("RESET");

    const handleReset = () => {
        const confirmed = window.confirm("Sei sicuro di voler resettare lo swabcode?");
        if (!confirmed) return;

        const randomCode = `RESET${Math.floor(1000 + Math.random() * 9000)}`;
        updateDnaTracker(
            dnaOrderId,
            TrackingStatus.TestDone,
            () => {
                refetchDnaTracker();
                updateSwabcode(patient.user_id, randomCode, () => { }, () => { });
            },
            console.error
        );
    };

    return shouldShowReset ? (
        <div className="space-y-[10px]">
            <div className="font-bold text-lg">Reset codici campione</div>
            <div className="text-xs">Puoi resettare lo swabcode, se presente</div>
            <div className="w-[200px] h-[40px]">
                <PrimaryButton
                    text={`Reset swabcode: ${patient.swabcode}`}
                    pointer
                    disabled={false}
                    onClick={handleReset}
                    fontSize={10}
                />
            </div>
        </div>
    ) : null;
};

const ChangeExpertSection = ({ patient }: { patient: Patient }) => {
    const { mutate: removePatient, isPending } = useRemovePatientFromMedicalUser()
    const { auth } = useAuth()
    const idToken = auth.idToken

    const { nutritionists } = useGetNutritionists()
    const { physicians } = useGetPhysicians()

    const [selectedNutritionist, setSelectedNutritionist] = useState<string>("");
    const [selectedPhysician, setSelectedPhysician] = useState<string>("");

    const assignNewExpert = async (
        role: "nutritionist" | "physician",
        newMedicalUserId: string,
        idToken: string,
    ) => {
        try {
            const result = await assignMedicalUserToPatient(newMedicalUserId, patient.user_id, idToken);
            if (result) {
                console.log(`${role.charAt(0).toUpperCase() + role.slice(1)} assigned successfully!`);
                if (role === "nutritionist") {
                    setSelectedNutritionist("");
                } else {
                    setSelectedPhysician("");
                }
            } else {
                console.error("Failed to assign new expert.");
            }
        } catch (error) {
            console.error("Error assigning new expert:", error);
        }
    };

    const handleUpdate = (role: "nutritionist" | "physician", email: string) => {
        if (!idToken) return
        const confirmed = window.confirm(`Vuoi cambiare il ${role} in ${email}?`);
        if (!confirmed) return;

        const currentMedicalUserId =
            role === "nutritionist"
                ? getMedicalUserIdByEmail(patient.nutritionist.email, nutritionists)
                : getMedicalUserIdByEmail(patient.physician.email, physicians)

        const newMedicalUserId =
            role === "nutritionist"
                ? getMedicalUserIdByEmail(email, nutritionists)
                : getMedicalUserIdByEmail(email, physicians);

        if (!newMedicalUserId) {
            console.error("Selected expert not found.");
            return;
        }

        if (currentMedicalUserId) {
            removePatient({
                idToken,
                medicalUserId: currentMedicalUserId,
                patientId: patient.user_id,
                onSuccess: () => {
                    console.log(`Removed patient from old ${role}, ready to assign new expert ${email}`);
                    assignNewExpert(role, newMedicalUserId, idToken);
                },
                onError: (error: string) => {
                    console.error(`Failed to remove patient: ${error}`);
                }
            }
            );
        } else {
            assignNewExpert(role, newMedicalUserId, idToken);
        }
    };

    return (
        <div className="space-y-[10px]">
            <div className="font-bold text-lg">Cambia esperti</div>
            <div className="text-xs">
                In quest'area puoi modificare gli esperti di <span className="text-holifya-blue">{patient.user_name}</span>
            </div>

            <ExpertSelector
                role="nutritionist"
                currentExpert={patient.nutritionist}
                experts={nutritionists}
                selectedExpert={selectedNutritionist}
                setSelectedExpert={setSelectedNutritionist}
                onUpdate={handleUpdate}
                disabled={isPending}
            />

            <ExpertSelector
                role="physician"
                currentExpert={patient.physician}
                experts={physicians}
                selectedExpert={selectedPhysician}
                setSelectedExpert={setSelectedPhysician}
                onUpdate={handleUpdate}
                disabled={isPending}
            />
        </div>
    );
};

interface ExpertSelectorProps {
    role: "nutritionist" | "physician";
    currentExpert: { name: string; family_name: string; email: string };
    experts: CalendlyExpert[]; // ideally typed
    selectedExpert: string;
    setSelectedExpert: (email: string) => void;
    onUpdate: (role: "nutritionist" | "physician", email: string) => void;
    disabled?: boolean;
};

const ExpertSelector: React.FC<ExpertSelectorProps> = ({
    role,
    currentExpert,
    selectedExpert,
    setSelectedExpert,
    experts,
    onUpdate,
    disabled = false,
}) => {
    return (
        <div>
            <div className="text-sm">{role === "nutritionist" ? "Nutrizionista" : "Endocrinologo"}</div>
            <div className="text-xs">
                Attuale: <span className="text-holifya-red">{currentExpert.name || "Nessuno"} {currentExpert.family_name}
                </span></div>
            <div className="flex gap-3 mt-2 items-center">
                <select
                    value={selectedExpert}
                    onChange={(e) => setSelectedExpert(e.target.value)}
                    className="border px-2 py-1 rounded text-xs w-[250px]"
                >
                    <option value="">Seleziona un nuovo {role === "nutritionist" ? "nutrizionista" : "endocrinologo"}</option>
                    {experts.filter(e => e.email !== currentExpert.email).map(e => (
                        <option key={e.email} value={e.email}>
                            {e.data.name} {e.data.family_name} ({e.email})
                        </option>
                    ))}
                </select>
                <div className="w-[200px] h-[30px]">
                    <PrimaryButton
                        text="Aggiorna"
                        pointer
                        disabled={!selectedExpert || disabled}
                        onClick={() => onUpdate(role, selectedExpert)}
                        fontSize={10}
                    />
                </div>
            </div>
        </div>
    )
}
